import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { JobPosting, JobApplication } from '@/data/jobOptions';

export function useJobPosting(jobId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['job-posting', jobId],
    queryFn: async () => {
      if (!jobId || !user) return null;

      // Get company for this user
      const { data: company } = await supabase
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .maybeSingle();

      if (!company) return null;

      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('id', jobId)
        .eq('company_id', company.id)
        .maybeSingle();

      if (error) throw error;
      return data as JobPosting | null;
    },
    enabled: !!jobId && !!user,
  });
}

export function useMyJobs(status?: 'active' | 'expired' | 'draft' | 'paused') {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-jobs', user?.id, status],
    queryFn: async () => {
      if (!user) return [];

      // Get company for this user
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .single();

      if (companyError || !company) return [];

      let query = supabase
        .from('job_postings')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data: jobs, error } = await query;

      if (error) throw error;

      // Get application counts for each job
      const jobIds = (jobs || []).map(j => j.id);
      
      if (jobIds.length === 0) return [];

      const { data: applications } = await supabase
        .from('job_applications')
        .select('job_posting_id')
        .in('job_posting_id', jobIds);

      // Count applications per job
      const applicationCounts: Record<string, number> = {};
      (applications || []).forEach(app => {
        applicationCounts[app.job_posting_id] = (applicationCounts[app.job_posting_id] || 0) + 1;
      });

      // Enrich jobs with application counts
      const enrichedJobs = (jobs || []).map(job => ({
        ...job,
        applications_count: applicationCounts[job.id] || 0,
      }));

      return enrichedJobs as (JobPosting & { applications_count: number })[];
    },
    enabled: !!user,
  });
}

export function useJobApplications(jobId: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['job-applications', jobId],
    queryFn: async () => {
      if (!jobId || !user) return [];

      // Verify job belongs to user's company
      const { data: company } = await supabase
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .maybeSingle();

      if (!company) return [];

      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_posting_id', jobId)
        .order('applied_at', { ascending: false });

      if (error) throw error;

      return data as JobApplication[];
    },
    enabled: !!jobId && !!user,
  });
}

export function useCreateJobPosting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (jobData: Partial<JobPosting> & { company_id: string }) => {
      // Generate slug
      const slug = jobData.title!
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') + '-' + Math.random().toString(36).substring(2, 8);

      // Calculate published_at and expires_at for active jobs
      const isActive = jobData.status === 'active';
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // +30 days

      const insertData = {
        company_id: jobData.company_id,
        title: jobData.title!,
        slug,
        description: jobData.description!,
        contract_type: jobData.contract_type!,
        salary_range_min: jobData.salary_range_min,
        salary_range_max: jobData.salary_range_max,
        salary_period: jobData.salary_period || 'annuale',
        work_mode: jobData.work_mode!,
        location_city: jobData.location_city!,
        location_region: jobData.location_region,
        required_skills: jobData.required_skills || [],
        nice_to_have_skills: jobData.nice_to_have_skills || [],
        inclusive_benefits: jobData.inclusive_benefits || [],
        inclusivity_tags: jobData.inclusivity_tags || [],
        application_method: jobData.application_method!,
        application_url: jobData.application_url,
        application_email: jobData.application_email,
        status: jobData.status || 'draft',
        published_at: isActive ? now.toISOString() : null,
        expires_at: isActive ? expiresAt.toISOString() : null,
      };

      const { data: job, error } = await supabase
        .from('job_postings')
        .insert(insertData as any)
        .select()
        .single();

      if (error) throw error;

      return job as JobPosting;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-jobs', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['job-credits', user?.id] });
    },
  });
}

export function useUpdateJobPosting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<JobPosting> & { id: string }) => {
      const { data: job, error } = await supabase
        .from('job_postings')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return job as JobPosting;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-jobs', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useDeleteJobPosting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('job_postings')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-jobs', user?.id] });
    },
  });
}
